import { useMemo } from 'react';
import {
	BigNum,
	PERCENTAGE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import computeSharpeRatioFromSnapshots from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/computeSharpeRatioFromSnapshots';
import useAppInitialLoad from 'src/hooks/app/useAppInitialLoad';
import usePortfolioOnChainMetrics from 'src/components/PortfolioPage/PortfolioTradeSubPage/hooks/usePortfolioOnChainMetrics';
import useLatchedValue from 'src/hooks/utils/useLatchedValue';
import usePortfolioTradingSnapshots from 'src/components/PortfolioPage/hooks/usePortfolioTradingSnapshots';
import computePortfolioTradeDelta from 'src/components/PortfolioPage/utils/computePortfolioTradeDelta';
import computePortfolioPnlValues from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/computePortfolioPnlValues';
import { useFundingPaymentsPerformance } from 'src/hooks/performance/useFundingPaymentsPerformance';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';

interface Params {
	period: PortfolioPeriod;
	includeFeesAndFunding: boolean;
	realizedPnlOnly: boolean;
}

export default function usePortfolioPerformanceStatsData({
	period: periodFromProps,
	includeFeesAndFunding,
	realizedPnlOnly,
}: Params) {
	const { isInitialLoading } = useAppInitialLoad();

	const { data, isLoading, ...rest } = usePortfolioTradingSnapshots({
		period: periodFromProps,
		maxSnapshots: 90,
		fillStrategy: 'repeatLastKnown',
	});

	const onChainMetricsFromHook = usePortfolioOnChainMetrics();

	const currentUserAccount = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()?.pubKey?.toString()
	);

	// Portfolio trading snapshots don't have data before Nov 1, 2025, so we use
	// the funding payments API for the 90d period to get accurate historical data.
	const useFundingPaymentsApi = periodFromProps === '90d';

	const {
		data: fundingPerformanceData,
		isLoading: isFundingPerformanceDataLoading,
	} = useFundingPaymentsPerformance({
		userAccount: useFundingPaymentsApi ? currentUserAccount : undefined,
		period: periodFromProps,
		marketFilter: 'all_markets',
	});

	const period = useLatchedValue({
		value: periodFromProps,
		isLatched: rest.isPlaceholderData,
	});

	const onChainMetrics = useLatchedValue({
		value: onChainMetricsFromHook,
		isLatched: rest.isPlaceholderData,
	});

	const {
		takerVolume,
		makerVolume,
		maxDrawdown,
		sharpeRatio,
		startOfPeriodTs,
		startOfPeriodCumulativeFunding,
	} = useMemo(() => {
		if (!data?.snapshots.length) {
			return {
				takerVolume: undefined,
				makerVolume: undefined,
				maxDrawdown: undefined,
				sharpeRatio: undefined,
				startOfPeriodTs: undefined,
				startOfPeriodCumulativeFunding: undefined,
			};
		}

		const { metrics, snapshots } = data;

		const startOfPeriodSnapshot = snapshots[0];

		const { takerVolume, makerVolume } = (() => {
			const currentTakerVolume = metrics.cumulativeTakerVolume;
			const startOfPeriodTakerVolume =
				startOfPeriodSnapshot.cumulativeTakerVolume;
			const takerVolumeDelta = computePortfolioTradeDelta(
				currentTakerVolume,
				startOfPeriodTakerVolume
			);

			const currentMakerVolume = metrics.cumulativeMakerVolume;
			const startOfPeriodMakerVolume =
				startOfPeriodSnapshot.cumulativeMakerVolume;
			const makerVolumeDelta = computePortfolioTradeDelta(
				currentMakerVolume,
				startOfPeriodMakerVolume
			);

			return {
				takerVolume: BigNum.fromPrint(
					takerVolumeDelta.toString(),
					QUOTE_PRECISION_EXP
				),
				makerVolume: BigNum.fromPrint(
					makerVolumeDelta.toString(),
					QUOTE_PRECISION_EXP
				),
			};
		})();

		const maxDrawdown = (() => {
			let peak =
				(startOfPeriodSnapshot.cumulativeRealizedPnl ?? 0) +
				(startOfPeriodSnapshot.unrealizedPnl ?? 0);

			let maxDrop = 0;
			for (let i = 1; i < snapshots.length; i++) {
				const pnl =
					(snapshots[i].cumulativeRealizedPnl ?? 0) +
					(snapshots[i].unrealizedPnl ?? 0);

				if (pnl > peak) {
					peak = pnl;
					continue;
				}

				const drop = peak - pnl;

				if (drop > maxDrop) {
					maxDrop = drop;
				}
			}

			return BigNum.fromPrint(maxDrop.toString(), QUOTE_PRECISION_EXP);
		})();

		const sharpeRatio = (() => {
			if (
				!snapshots ||
				snapshots.length < 3 ||
				period === '24h' ||
				period === '7d'
			) {
				return;
			}

			const result = computeSharpeRatioFromSnapshots({ snapshots });

			return BigNum.fromPrint(result.toString(), PERCENTAGE_PRECISION_EXP);
		})();

		return {
			takerVolume,
			makerVolume,
			maxDrawdown,
			sharpeRatio,
			startOfPeriodTs: startOfPeriodSnapshot.ts,
			startOfPeriodCumulativeFunding: startOfPeriodSnapshot.cumulativeFunding,
		};
	}, [data, period]);

	const pnlChange = useMemo(() => {
		if (!data?.snapshots?.length || !data?.metrics) {
			return;
		}

		const { snapshots, metrics } = data;

		const startOfPeriodSnapshot = snapshots[0];

		const { totalPnl: endOfPeriodTotalPnl } = computePortfolioPnlValues({
			cumulativeRealizedPnl: metrics.cumulativeRealizedPnl,
			unrealizedPnl: onChainMetrics.currentUnrealizedPnl,
			cumulativeFunding: onChainMetrics.currentCumulativeFunding,
			cumulativeFeePaid: metrics.cumulativeFeePaid,
			cumulativeFeeRebate: metrics.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

		const { totalPnl: startOfPeriodTotalPnl } = computePortfolioPnlValues({
			cumulativeRealizedPnl: startOfPeriodSnapshot.cumulativeRealizedPnl,
			unrealizedPnl: startOfPeriodSnapshot.unrealizedPnl,
			cumulativeFunding: startOfPeriodSnapshot.cumulativeFunding,
			cumulativeFeePaid: startOfPeriodSnapshot.cumulativeFeePaid,
			cumulativeFeeRebate: startOfPeriodSnapshot.cumulativeFeeRebate,
			includeFeesAndFunding,
			realizedPnlOnly,
		});

		const delta = computePortfolioTradeDelta(
			endOfPeriodTotalPnl,
			startOfPeriodTotalPnl
		);

		return BigNum.fromPrint(delta.toString(), QUOTE_PRECISION_EXP);
	}, [data, onChainMetrics, includeFeesAndFunding, realizedPnlOnly]);

	const netFundingFromFundingPayments = useMemo(() => {
		const marketData = fundingPerformanceData?.[0];

		// Map period to the funding performance data key.
		const periodKey = period === '90d' ? 'all' : period;
		const periodData = marketData?.performance[periodKey];

		if (!periodData?.length) {
			return;
		}

		// The last point contains the cumulative funding for the period.
		const lastPoint = periodData[periodData.length - 1];
		const totalFunding = lastPoint?.totalPnl ?? 0;

		return BigNum.fromPrint(totalFunding.toString(), QUOTE_PRECISION_EXP);
	}, [fundingPerformanceData, period]);

	const netFundingFromSnapshots = useMemo(() => {
		if (
			onChainMetrics.currentCumulativeFunding === undefined ||
			startOfPeriodCumulativeFunding === undefined
		) {
			return;
		}

		const delta = computePortfolioTradeDelta(
			onChainMetrics.currentCumulativeFunding,
			startOfPeriodCumulativeFunding
		);

		return BigNum.fromPrint(delta.toString(), QUOTE_PRECISION_EXP);
	}, [onChainMetrics.currentCumulativeFunding, startOfPeriodCumulativeFunding]);

	const fundingPayments = (() => {
		if (!currentUserAccount) {
			return;
		}

		if (useFundingPaymentsApi) {
			return netFundingFromFundingPayments;
		}

		return netFundingFromSnapshots;
	})();

	const netFunding = useLatchedValue({
		value: fundingPayments,
		// Latching this to `isPlaceholderData` so the net funding value doesn't
		// prematurely update while the rest of the data is in a transition state.
		isLatched: rest.isPlaceholderData,
	});

	return {
		data: {
			pnlChange,
			takerVolume,
			makerVolume,
			maxDrawdown,
			sharpeRatio,
			startOfPeriodTs,
			netFunding,
		},
		isLoading: isLoading || isInitialLoading,
		isFundingDataLoading: isFundingPerformanceDataLoading || isInitialLoading,
		...rest,
	};
}
